import { t } from 'i18next'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

import {
  CreateProductPreviewCurrentProductFragment,
  CreateProductPreviewSelectedCategoryFragment,
} from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { Maybe } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { Input as FormatProductInput } from '@/modules/requisitions/utils/formatProductItem'
import { CreateProductFormInputs } from '@/modules/shared/components/create-product-modal/CreateProductFormSchema'
import { ProductImage } from '@/modules/shared/components/table/table-column-items/shared/ProductImage'
import { ProductItem } from '@/modules/shared/components/table/table-column-items/shared/ProductItem'

interface CreateProductPreviewProps {
  selectedCategory?: CreateProductPreviewSelectedCategoryFragment | null
  formatProduct?: (input: FormatProductInput) => string
  image?: Maybe<string>
  currentProduct?: CreateProductPreviewCurrentProductFragment | null
  description?: string
}

export default function CreateProductPreview({
  selectedCategory,
  image,
  formatProduct,
  currentProduct,
  description,
}: CreateProductPreviewProps) {
  const formMethods = useFormContext<CreateProductFormInputs>()

  const product: CreateProductPreviewCurrentProductFragment = useMemo(() => {
    // Provide default values to match the fragment type requirements
    const baseProduct = {
      id: 0,
      image: null,
      itemDescription: null,
      brand: null,
      itemSize: null,
      itemMeasure: null,
      itemPackName: null,
      itemSellQuantity: null,
      itemSellPackName: null,
      concatenatedSellUnit: null,
      categoryId: null,
      ...currentProduct, // Override with actual values if currentProduct exists
    }

    if (formMethods) {
      const formValues = formMethods.getValues() || {}
      const getConcatenatedSellUnit = () => {
        const packName = formValues.itemSellPackName
        const quantity = formValues.itemSellQuantity

        if (!packName && !quantity) {
          return null
        }

        if (packName && !quantity) {
          return packName
        }

        if (!packName && quantity) {
          return `Each of ${quantity}`
        }

        return `${packName} of ${quantity}`
      }

      const formProduct = {
        brand: formValues.brand || '',
        itemDescription: formValues.itemDescription,
        itemSize: formValues.itemSize,
        itemMeasure: formValues.itemMeasure,
        itemPackName: formValues.itemPackName,
        itemSellQuantity: formValues.itemSellQuantity,
        itemSellPackName: formValues.itemSellPackName,
        concatenatedSellUnit: getConcatenatedSellUnit(),
      }

      Object.assign(baseProduct, formProduct)
    }

    return {
      ...baseProduct,
    }
  }, [currentProduct, formMethods])

  formMethods?.watch()

  return (
    <div>
      <p className="font-bold">{t('general.productPreview', 'Product Preview')}</p>
      <p className="mb-3 text-gray-500">
        {description ||
          t(
            'createProduct.productPreview.description',
            'As you complete the fields above, a preview of the product will appear here.'
          )}
      </p>
      <div className="flex gap-x-2 rounded-md border bg-white p-4">
        <ProductImage image={image || product?.image || ''} />
        <div className="flex flex-col justify-center">
          <ProductItem product={product} formatProduct={formatProduct} />
          <p className="text-xs">
            {selectedCategory &&
              `${selectedCategory?.parent?.name ? `${selectedCategory?.parent?.name} > ` : ''}${selectedCategory?.name}`}
          </p>
        </div>
      </div>
    </div>
  )
}
